import { Currency } from 'hx-services';
import type { TranslocoService } from '@jsverse/transloco';
import type { Locale } from 'date-fns';
import { format, parseISO } from 'date-fns';
import { enUS, kk, ru } from 'date-fns/locale';
import { pluralRu } from '@callcenter-app/util/plural';
import { ArchiveOrderItem, CurrencyAmount } from '@callcenter-app/service/orders-archive.service';

/**
 * Pure formatting helpers for «Архив заказов» — money by currency, ru/kk/en plurals, rating
 * colour scale. Kept free of Angular/DOM so the repo's «no function calls in templates» rule
 * is satisfied by pre-computing view-model strings in component code (see orders-archive.*.spec.ts).
 */

const CURRENCY_SYMBOL: Record<Currency, string> = {
  KZT: '₸',
  RUB: '₽',
  KGS: 'сом',
  UZS: 'so\'m',
  TJS: 'смн',
  USD: '$',
};

/** What a number the server did not send (or sent as something that is not a number) is printed as — never the word «undefined», «null» or «NaN». */
export const NO_NUMBER = '—';

/**
 * ru-RU grouped thousands, ROUNDED to whole units — for counts and for the AGGREGATES over many orders (the tiles, the sums of a group, the client's «всего на сумму»). A sum of ONE order
 * or one booking is not rounded: that is {@link formatNumberExact}. A value that is not a finite number — a field a real row does not have (a reservation has no total yet), `null`,
 * `NaN` — is {@link NO_NUMBER}: the type says `number`, the wire has the last word.
 */
export function formatNumber(n: number | null | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? Math.round(n).toLocaleString('ru-RU') : NO_NUMBER;
}

/**
 * The symbol of a currency. No currency (an order that is only a reservation has none: `hc_orders.currency_` is empty until the order is made) — NOTHING: an empty string,
 * never «undefined». A code the table does not know is printed as it is.
 */
export function currencySymbol(currency?: Currency | string | null): string {
  const code = typeof currency === 'string' ? currency.trim() : '';
  return code ? (CURRENCY_SYMBOL[code as Currency] ?? code) : '';
}

/**
 * «9 500 ₸», rounded to whole units — for an AGGREGATE over many orders. The sum of one order or one booking is {@link formatMoneyExact}: the number in the list, in the drawer and on the card is
 * one number. With no currency only the number is printed («9 500»), with no amount only the dash — never a symbol next to a dash, never the word «undefined» (a reservation of an operator is
 * stored without a currency, and the server sends none).
 */
export function formatMoney(amount: number | null | undefined, currency?: Currency | string | null): string {
  return withCurrency(formatNumber(amount), currency);
}

/**
 * {@link formatNumber} that keeps the tiyn: «3 607,5». The money is not always whole — a 7,5 % discount on 3 900 is 292,5, the order to pay is 5 107,5 — and a sum of ONE order or booking is
 * printed as the server has it, everywhere (the row of the list, the drawer, the card, the payments, the history, the editor): round it somewhere and the same order has two numbers. Whole
 * amounts print as `formatNumber` prints them; a fraction has a comma, two places at most, no trailing zero.
 */
export function formatNumberExact(n: number | null | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString('ru-RU', { maximumFractionDigits: 2 }) : NO_NUMBER;
}

/** {@link formatMoney} on {@link formatNumberExact}: «3 607,5 ₸», «5 400 ₸». For every sum of ONE order or one booking. */
export function formatMoneyExact(amount: number | null | undefined, currency?: Currency | string | null): string {
  return withCurrency(formatNumberExact(amount), currency);
}

function withCurrency(sum: string, currency?: Currency | string | null): string {
  const symbol = sum === NO_NUMBER ? '' : currencySymbol(currency);
  return symbol ? `${sum} ${symbol}` : sum;
}

/**
 * A number with a fraction as the language writes it: «7,5» (ru, kk), «7.5» (en); two places at most, no trailing zero, a whole number has none. The 7,5 % of a discount is
 * «−7,5%» on a Russian screen, not «−7.5%».
 */
export function formatFraction(n: number | null | undefined, lang: string): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) {
    return NO_NUMBER;
  }
  const text = n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  return lang === 'en' ? text.replace(',', '.') : text;
}

/**
 * §F58: the «main»/headline currency (and the order of the rest) is picked by NUMBER OF ORDERS
 * (`orders` desc, tie → `amount` desc) — never by the raw amount, which is meaningless across
 * currencies with no FX (265 000 UZS from a single order is not "bigger" than 142 278 KZT from ten).
 * Every caller (KPI «Сумма по валютам» tile, the «Найдено» currency chips, day-group meta, client-mode
 * month meta) goes through this one function so they all agree on both the headline and the ordering
 * of the rest.
 */
export function sortCurrencyAmounts(amounts: CurrencyAmount[]): CurrencyAmount[] {
  return [...amounts].filter(a => a.amount !== 0).sort((a, b) => b.orders - a.orders || b.amount - a.amount);
}

/**
 * «Сумма по валютам»: the currency with the most orders is the headline, the rest are secondary
 * lines, in the same orders-desc order — matches the canvas KPI tile (Main.dc.html `sumValue`/`sumLines`).
 */
export function formatCurrencyAmounts(amounts: CurrencyAmount[]): { main?: string; lines: string[] } {
  const sorted = sortCurrencyAmounts(amounts);
  if (!sorted.length) {
    return { main: undefined, lines: [] };
  }
  return {
    main: formatMoney(sorted[0].amount, sorted[0].currency),
    lines: sorted.slice(1).map(a => formatMoney(a.amount, a.currency)),
  };
}

/** Средняя оценка отзыва: ≤2.5 красный, <4 оранжевый, иначе зелёный (Row.dc.html `fbFg`). */
export function ratingColorClass(avg?: number): 'archive-rating--none' | 'archive-rating--bad' | 'archive-rating--mid' | 'archive-rating--good' {
  if (avg === undefined || avg === null) {
    return 'archive-rating--none';
  }
  if (avg <= 2.5) {
    return 'archive-rating--bad';
  }
  if (avg < 4) {
    return 'archive-rating--mid';
  }
  return 'archive-rating--good';
}

export interface PluralParams {
  n: number;
  word: string;
}

/** ru forms tuple: [1 (один), 2 (два-четыре), 5 (пять+/11-14)] — same order pluralRu expects. */
export type RuForms = readonly [string, string, string];

/**
 * `{n, word}` param pair for a `{{n}} {{word}}` i18n template. kk has no grammatical number
 * agreement (word never changes) and en only distinguishes 1 vs many — both resolved here so
 * every ordersArchive.* count string in ru/kk/en shares one call site (history 2026-08-22 retro #6:
 * a ru-only plural helper misused for kk/en silently prints the wrong form).
 */
export function pluralParams(lang: string, n: number, forms: { ru: RuForms; kk: string; en: readonly [string, string] }): PluralParams {
  if (lang === 'kk') {
    return { n, word: forms.kk };
  }
  if (lang === 'en') {
    return { n, word: n === 1 ? forms.en[0] : forms.en[1] };
  }
  return { n, word: pluralRu(n, forms.ru) };
}

export const ORDER_WORD = { ru: ['заказ', 'заказа', 'заказов'] as RuForms, kk: 'тапсырыс', en: ['order', 'orders'] as const };
export const DAY_WORD = { ru: ['день', 'дня', 'дней'] as RuForms, kk: 'күн', en: ['day', 'days'] as const };
export const PHOTO_WORD = { ru: ['фото', 'фото', 'фото'] as RuForms, kk: 'фото', en: ['photo', 'photos'] as const };
export const FILE_WORD = { ru: ['файл', 'файла', 'файлов'] as RuForms, kk: 'файл', en: ['file', 'files'] as const };
export const COMMENT_WORD = { ru: ['комментарий', 'комментария', 'комментариев'] as RuForms, kk: 'пікір', en: ['comment', 'comments'] as const };
export const CALL_WORD = { ru: ['звонок', 'звонка', 'звонков'] as RuForms, kk: 'қоңырау', en: ['call', 'calls'] as const };
export const TIME_WORD = { ru: ['раз', 'раза', 'раз'] as RuForms, kk: 'рет', en: ['time', 'times'] as const };
export const REFUND_ORDER_WORD = { ru: ['отменён', 'отменено', 'отменено'] as RuForms, kk: 'бас тартылды', en: ['cancelled', 'cancelled'] as const };
export const RECEIVED_ORDER_WORD = { ru: ['выполнен', 'выполнено', 'выполнено'] as RuForms, kk: 'орындалды', en: ['completed', 'completed'] as const };
export const FIELD_WORD = { ru: ['поле', 'поля', 'полей'] as RuForms, kk: 'өріс', en: ['field', 'fields'] as const };
export const ITEM_WORD = { ru: ['позиция', 'позиции', 'позиций'] as RuForms, kk: 'позиция', en: ['item', 'items'] as const };
export const DISCOUNT_WORD = { ru: ['скидка', 'скидки', 'скидок'] as RuForms, kk: 'жеңілдік', en: ['discount', 'discounts'] as const };
export const CURRENCY_WORD = { ru: ['валюта', 'валюты', 'валют'] as RuForms, kk: 'валюта', en: ['currency', 'currencies'] as const };
export const SALE_WORD = { ru: ['продажа', 'продажи', 'продаж'] as RuForms, kk: 'сатылым', en: ['sale', 'sales'] as const };
/** Locative/prepositional plural: «в 1 заказе» vs «в 2/5 заказах» — the plural bucket (2-4 vs 5+) shares
 * the same prepositional form in Russian, unlike the nominative ORDER_WORD above. */
export const ORDER_WORD_PREP = { ru: ['заказе', 'заказах', 'заказах'] as RuForms, kk: 'тапсырыс', en: ['order', 'orders'] as const };

/** Short "Имя Ф." form for a full name (row/drawer chips) — falls back to the phone-like input as-is. */
export function shortName(fullname?: string): string | undefined {
  if (!fullname) {
    return undefined;
  }
  const parts = fullname.trim().split(/\s+/);
  if (parts.length < 2) {
    return fullname;
  }
  const initial = parts[1].charAt(0);
  return initial ? `${parts[0]} ${initial}.` : fullname;
}

/** date-fns locale for the active Transloco language (precedent: monitoring-sales.component.ts). */
export function dateFnsLocale(lang: string): Locale {
  if (lang === 'kk') {
    return kk;
  }
  if (lang === 'en') {
    return enUS;
  }
  return ru;
}

/**
 * §F53: client-mode month group title — «Сентябрь 2026» (nominative, capitalised). date-fns's
 * `LLLL yyyy` already gives the (correct, standalone/nominative) month name per locale, but ru/kk
 * print it lower-case — capitalise the first letter for every locale rather than special-casing ru/kk.
 */
export function monthTitle(monthKey: string, lang: string): string {
  const date = parseISO(monthKey + '-01');
  const raw = format(date, 'LLLL yyyy', { locale: dateFnsLocale(lang) });
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/**
 * The calendar day of a date the server sends («1990-09-28», or a datetime that starts with the day) as its own digits (`month` is 1–12), never through
 * `new Date(...)`: a date without a zone is parsed as the UTC midnight, and read back with the local getters it is the DAY BEFORE anywhere west of UTC
 * (QA F-4). `undefined` for anything that does not start with `YYYY-MM-DD`.
 */
export function calendarDateParts(value: string): { year: number; month: number; day: number } | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  return match ? { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) } : undefined;
}

/** Days from `now` to the next occurrence of `birthDate`'s month/day (0 = today, wraps to next year). A date without a zone is a calendar day, in any time zone. */
export function daysUntilBirthday(birthDate: string, now: Date = new Date()): number {
  const parts = calendarDateParts(birthDate);
  const fallback = parts ? undefined : new Date(birthDate);
  const month = parts ? parts.month - 1 : fallback?.getMonth() ?? NaN;
  const day = parts ? parts.day : fallback?.getDate() ?? NaN;
  const next = new Date(now.getFullYear(), month, day);
  next.setHours(0, 0, 0, 0);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (next.getTime() < today.getTime()) {
    next.setFullYear(next.getFullYear() + 1);
  }
  return Math.round((next.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
}

const ISO_WALL_CLOCK_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?/;

/**
 * Parses an ISO datetime as WALL-CLOCK digits (ignoring any trailing offset/`Z`) and returns a `Date`
 * whose *local* getters reproduce those exact digits — i.e. `format()`-ing the result always prints the
 * STORE's own wall time, never the viewer's browser timezone. Backend sends the store's offset baked in
 * (e.g. `2026-09-28T11:02:00+06:00`); converting that to the browser's zone silently shows the wrong
 * hour for any operator not in that same zone (every on-page timestamp must go through this — §30).
 */
export function parseStoreLocal(iso: string): Date {
  const m = ISO_WALL_CLOCK_RE.exec(iso);
  if (!m) {
    return parseISO(iso);
  }
  const [, y, mo, d, h, mi, s] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), s ? Number(s) : 0);
}

/** `format(parseStoreLocal(iso), pattern, options)` — the one call every on-page timestamp should use. */
export function formatStoreLocal(iso: string, pattern: string, options?: { locale?: Locale }): string {
  return format(parseStoreLocal(iso), pattern, options);
}

/**
 * «23.09 – 29.09.2026» — dd.MM (MM.dd for `en`), year shown once at the end unless the range crosses a
 * year boundary, in which case each side gets its own year.
 */
export function formatPeriodRange(from: string, to: string, lang: string): string {
  const fromDate = parseISO(from);
  const toDate = parseISO(to);
  const pattern = lang === 'en' ? 'MM.dd' : 'dd.MM';
  const fromText = format(fromDate, pattern);
  const toText = format(toDate, pattern);
  const fromYear = fromDate.getFullYear();
  const toYear = toDate.getFullYear();
  if (fromYear === toYear) {
    return `${fromText} – ${toText}.${toYear}`;
  }
  return `${fromText}.${fromYear} – ${toText}.${toYear}`;
}

/** Kazakhstan/Russia (+7), Kyrgyzstan (+996), Uzbekistan (+998), Tajikistan (+992) — the only country
 *  codes this network operates in. Longest prefix first so a +996 number isn't mistaken for +7X. */
const PHONE_COUNTRY_CODES = ['996', '998', '992', '7'];

/**
 * §F64: national-number digit grouping per country code — +7's 10 national digits split 3-3-2-2
 * («701 123 45 67»); +998's 9 split 2-3-2-2 («00 055 50 15»); +996/+992's 9 split 3-3-3
 * («000 555 014»). A single fixed 3-3-2-2/10-digit shape (the old behaviour) silently fell back to
 * the raw unformatted string for every +998/+996/+992 number — the group SIZES, not just the digit
 * count, differ per code, so each one is listed explicitly rather than derived.
 */
const PHONE_GROUPING: Record<string, number[]> = {
  '7': [3, 3, 2, 2],
  '998': [2, 3, 2, 2],
  '996': [3, 3, 3],
  '992': [3, 3, 3],
};

/**
 * «77011234567» → «+7 701 123 45 67», «998000555015» → «+998 00 055 50 15», «996000555014» →
 * «+996 000 555 014» — falls back to a bare `+`-prefixed string for anything that doesn't match a
 * known code + its exact national-number length (short codes, foreign numbers, garbage input) rather
 * than guessing at a wrong grouping.
 */
export function formatPhone(phone?: string | null): string | undefined {
  const raw = phone?.trim();
  if (!raw) {
    return undefined;
  }
  const digits = raw.replace(/[^0-9]/g, '');
  const code = PHONE_COUNTRY_CODES.find(c => digits.startsWith(c));
  const grouping = code ? PHONE_GROUPING[code] : undefined;
  const rest = code ? digits.slice(code.length) : '';
  const expectedLength = grouping?.reduce((a, b) => a + b, 0);
  if (!code || !grouping || rest.length !== expectedLength) {
    return raw.startsWith('+') ? raw : '+' + digits;
  }
  const groups: string[] = [];
  let pos = 0;
  for (const size of grouping) {
    groups.push(rest.slice(pos, pos + size));
    pos += size;
  }
  return `+${code} ${groups.join(' ')}`;
}

/**
 * Diff/history file chips — compact type label: Фото for images, PDF/DOC/XLS by mime family, else the
 * raw extension, else `genericLabel` (i18n «Файл»/File). Kept separate from archive-order-files' own
 * per-file extension display (that one shows the exact extension, e.g. "DOCX", extension FIRST — this
 * is a coarser, history-log taxonomy per spec, mime family first).
 * Robustness (security re-check R3): a malformed/legacy row can arrive with no `fileName` and/or no
 * `mimeType` at all — never throws on that (unlike a bare `fileName.lastIndexOf('.')`), and never shows
 * a blank chip; falls all the way through to `genericLabel` instead.
 */
export function fileTypeLabel(fileName: string | undefined, mimeType: string | undefined, photoLabel: string, genericLabel: string): string {
  const mime = (mimeType ?? '').toLowerCase();
  if (mime.startsWith('image/')) {
    return photoLabel;
  }
  if (mime === 'application/pdf') {
    return 'PDF';
  }
  if (mime.includes('word')) {
    return 'DOC';
  }
  if (mime.includes('sheet') || mime.includes('excel')) {
    return 'XLS';
  }
  const ext = fileExtension(fileName);
  return ext ? ext.toUpperCase() : genericLabel;
}

/** Grams → «250 г» / «1,8 кг» (comma for ru/kk, period for en) — same numeric value `HxWeightPipe`
 * (hx-services) itself computes (grams under 500 as-is, ÷1000 above, no rounding either way), just
 * localises the separator and the unit word; that shared pipe hardcodes a raw `value/1000` (period,
 * whatever the active language) and Russian-only «кг»/«гр», which needs the shared-lib approval gate
 * (CLAUDE.md guardrails) to fix at the source — this page formats weight itself instead of going
 * through it, same precedent as `formatFileSize` below. */
export function formatItemWeight(grams: number | undefined, lang: string): string {
  if (!grams) {
    return '';
  }
  if (grams < 500) {
    return `${grams} ${lang === 'en' ? 'g' : 'г'}`;
  }
  const kg = String(grams / 1000);
  return `${lang === 'en' ? kg : kg.replace('.', ',')} ${lang === 'en' ? 'kg' : 'кг'}`;
}

/** The kinds of line that are not goods: they are not weighed, whatever the field holds (the real source keeps a `1` there for a delivery — «1 г»). */
const NOT_WEIGHED_TYPES: readonly string[] = ['DELIVERY', 'SERVICE', 'NON_MATERIAL_ASSETS'];

/** {@link formatItemWeight} of a line of an order — empty for a delivery and a service, which have no weight to say. */
export function itemWeightText(item: Pick<ArchiveOrderItem, 'type' | 'weight'>, lang: string): string {
  return NOT_WEIGHED_TYPES.includes(item.type) ? '' : formatItemWeight(item.weight, lang);
}

/** Bytes → «93 КБ» (whole numbers under 1 MB) or «1,1 МБ» (one decimal above, comma separator for
 *  ru/kk, period for en) — never a raw byte/1024 count once it crosses 1024 KB (§F37). */
export function formatFileSize(bytes: number, lang: string): string {
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${Math.round(kb)} ${lang === 'en' ? 'KB' : 'КБ'}`;
  }
  const mb = (kb / 1024).toFixed(1);
  return `${lang === 'en' ? mb : mb.replace('.', ',')} ${lang === 'en' ? 'MB' : 'МБ'}`;
}

/** «+06:00» → «UTC+6»; «+05:30» → «UTC+5:30» (keeps non-zero minutes, drops the leading zero/":00"). */
export function formatUtcOffset(raw: string): string {
  const m = /^([+-])(\d{1,2}):?(\d{2})?/.exec(raw.trim());
  if (!m) {
    return raw;
  }
  const sign = m[1];
  const hours = Number(m[2]);
  const minutes = m[3] ? Number(m[3]) : 0;
  return `UTC${sign}${hours}${minutes ? ':' + String(minutes).padStart(2, '0') : ''}`;
}

/** «ORDER_RETURNED» → «ORDER RETURNED» — a readable fallback when an enum value has no i18n key of
 *  its own (§F57): callers show `${genericLabel} · humanizeEnumValue(raw)}` instead of ever letting the
 *  raw dotted transloco key ("ordersArchive.eventType.ORDER_RETURNED") leak onto the screen. */
export function humanizeEnumValue(value: string): string {
  return value.replace(/_/g, ' ');
}

/**
 * §F57-ext: the same "never show a raw i18n key" fallback as `archive-history.component.ts`'s
 * `toEventLabel` (§F57), generalised for the page's other small enum-ish lookups — `actorType.`,
 * `enum.deliveryType.`, `order.documentType.`, `paymentType.` — none of which have eventType's
 * "· timeline entry" framing, so the fallback is just the humanized raw value, no generic-label prefix.
 * `translate()` returns the key itself when there's no match — the one detection method usable from
 * plain TS/without inspecting Transloco's internal translation map.
 */
export function translateEnumOrHumanize(tr: TranslocoService, key: string, raw: string, lang?: string): string {
  const translated = tr.translate(key, {}, lang);
  return translated !== key ? translated : humanizeEnumValue(raw);
}

const DELIVERY_TIME_RANGE_WITH_TIME_KEY: Record<string, string> = {
  ANY_TIME: 'ordersArchive.enum.deliveryTimeRange.ANY_TIME',
  AFTER: 'ordersArchive.enum.deliveryTimeRange.afterAt',
  BEFORE: 'ordersArchive.enum.deliveryTimeRange.beforeAt',
  ON_TIME: 'ordersArchive.enum.deliveryTimeRange.onTimeAt',
};

const DELIVERY_TIME_RANGE_NO_TIME_KEY: Record<string, string> = {
  ANY_TIME: 'ordersArchive.enum.deliveryTimeRange.ANY_TIME',
  AFTER: 'ordersArchive.enum.deliveryTimeRange.afterNoTime',
  BEFORE: 'ordersArchive.enum.deliveryTimeRange.beforeNoTime',
  ON_TIME: 'ordersArchive.enum.deliveryTimeRange.onTimeNoTime',
};

/**
 * §F71: «время для курьера» — with a time, a complete phrase («ровно к 19:00» / «до 19:00» /
 * «после 19:00»); without one (older data, or the plain-enum diff shape before the backend ships the
 * structured `{range, time}` value), a complete phrase with NO dangling preposition («точно ко
 * времени» / «до определённого времени» / «после определённого времени») — never the bare enum word
 * («ровно к») that used to render with nothing after it. `ANY_TIME` never takes a time either way.
 */
export function deliveryTimeRangeText(tr: TranslocoService, range: string, time?: string): string {
  const key = (time ? DELIVERY_TIME_RANGE_WITH_TIME_KEY : DELIVERY_TIME_RANGE_NO_TIME_KEY)[range];
  if (!key) {
    return translateEnumOrHumanize(tr, 'ordersArchive.enum.deliveryTimeRange.' + range, range);
  }
  return tr.translate(key, { time: time ?? '' });
}

const SAFE_RASTER_IMAGE_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif']);
const SAFE_RASTER_IMAGE_EXT = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif']);

/** Robustness (R3): tolerates a missing/empty `fileName` (legacy/malformed row) instead of throwing on
 *  `fileName.lastIndexOf('.')` — treated the same as "no extension", same as a name with no dot. */
export function fileExtension(fileName?: string): string | undefined {
  if (!fileName) {
    return undefined;
  }
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(dot + 1).toLowerCase() : undefined;
}

/**
 * Security (M-2): a fixed raster-image allow-list — never a bare `mime.startsWith('image/')`, which
 * also matches `image/svg+xml`. `<img>` itself never executes an SVG's embedded `<script>` (browsers
 * disable scripting for image-context resources), but this app also offers «Открыть оригинал» / a plain
 * anchor to the SAME guid URL — opened directly (top-level navigation, not inside `<img>`), an SVG/HTML
 * response executes script in this app's own origin (`/api/vn/files/download/{guid}`). Gating the
 * allow-list once here keeps the viewer, the decor/legal file tiles and any future consumer consistent.
 * Requires the mime AND extension to agree when an extension is present (a mislabelled upload falls
 * back to download-only rather than a guessed preview). Robustness (R3): a missing `fileName` can't be
 * verified against its extension at all — always download-only in that case, never a guessed preview.
 */
export function isSafeRasterImage(mimeType: string | undefined, fileName?: string): boolean {
  if (!fileName) {
    return false;
  }
  const mime = (mimeType ?? '').toLowerCase();
  if (!SAFE_RASTER_IMAGE_MIME.has(mime)) {
    return false;
  }
  const ext = fileExtension(fileName);
  return !ext || SAFE_RASTER_IMAGE_EXT.has(ext);
}

/** Security (M-2): PDF preview/open requires the mime type AND extension to agree — see {@link isSafeRasterImage}.
 *  Robustness (R3): a missing `fileName` is always download-only, same reasoning as `isSafeRasterImage`. */
export function isSafePdf(mimeType: string | undefined, fileName?: string): boolean {
  return !!fileName && (mimeType ?? '').toLowerCase() === 'application/pdf' && fileExtension(fileName) === 'pdf';
}

/** Two-letter avatar initials from a full name — "Алия Бекова" → "АБ". */
export function initialsOf(fullname?: string): string {
  if (!fullname) {
    return '?';
  }
  const parts = fullname.trim().split(/\s+/).filter(p => !!p);
  const letters = parts.slice(0, 2).map(p => p.charAt(0).toUpperCase());
  return letters.join('') || '?';
}

/**
 * ONE place that knows the URL of the new full order-card page (`/order/:id`) — shared by every
 * "Ctrl/⌘-click opens the full card in a new tab" affordance in the archive (row, drawer ↗, the
 * payment tab's refund-order link, the receiving tab's transferred-order link) and by the page
 * itself when it links to a related order (refund/transfer). Kept distinct from the OLD view's
 * `/orders/:id` (still used by the in-progress "Открыть в «Заказах»" banner, which is unaffected).
 */
export function orderCardRoute(id: number): Array<string | number> {
  return ['/order', id];
}

/** `NavigationExtras.state` key the archive uses to hand the full order-card page its exact
 * `/orders-archive` query params (read back via `Location.getState()`) so the breadcrumb's
 * «Архив заказов» link returns to the same search/filters instead of a bare `/orders-archive`. */
export const ORDER_CARD_BACK_STATE_KEY = 'archiveBack';

/** The same for the «Заказы» page (1d): its exact `/orders` query params — view, filters, the open drawer — so the card's «Заказы» crumb
 * returns to the list the operator came from, with the same drawer open, instead of a bare `/orders`. */
export const ORDERS_CARD_BACK_STATE_KEY = 'ordersBack';
